import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../contexts/I18nContext";
import { api } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, Clock } from "lucide-react";
import { getApiPrefix } from "../utils/apiHelper";
import { handleApiError } from "../utils/errorHandler";

const MESI = [
  { num: '01', label: 'Gennaio' }, { num: '02', label: 'Febbraio' },
  { num: '03', label: 'Marzo' }, { num: '04', label: 'Aprile' },
  { num: '05', label: 'Maggio' }, { num: '06', label: 'Giugno' },
  { num: '07', label: 'Luglio' }, { num: '08', label: 'Agosto' },
  { num: '09', label: 'Settembre' }, { num: '10', label: 'Ottobre' },
  { num: '11', label: 'Novembre' }, { num: '12', label: 'Dicembre' },
];

export default function ManodoperaCdcPage() {
  const { t } = useTranslation();
  const [anno, setAnno] = useState(String(new Date().getFullYear()));
  const [centriCosto, setCentriCosto] = useState([]);
  const [recordsByKey, setRecordsByKey] = useState({}); // {ccid_mese: {ore, note, id}}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const annoOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    return [cy - 2, cy - 1, cy, cy + 1].map(String);
  }, []);

  const fetchCentriCosto = async () => {
    try {
      const res = await api.get(`${getApiPrefix()}/cost-centers`);
      setCentriCosto(Array.isArray(res.data) ? res.data : []);
    } catch (e) { handleApiError(e); }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${getApiPrefix()}/cdc-manodopera`, { params: { anno } });
      const map = {};
      (res.data || []).forEach(r => {
        map[`${r.centro_costo_id}_${r.mese}`] = { ore: r.ore, note: r.note || "", id: r.id };
      });
      setRecordsByKey(map);
    } catch (e) { handleApiError(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchCentriCosto(); }, []);
  useEffect(() => { fetchRecords(); }, [anno]);

  const setOre = (ccid, mese, val) => {
    const key = `${ccid}_${mese}`;
    setRecordsByKey(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ore: val }
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const items = [];
      Object.entries(recordsByKey).forEach(([key, val]) => {
        if (!val || val.ore === "" || val.ore == null) return;
        const [centro_costo_id, mese] = key.split("_");
        const ore = parseFloat(String(val.ore).replace(",", "."));
        if (!isNaN(ore) && ore >= 0) {
          items.push({ centro_costo_id, mese, ore, note: val.note || null });
        }
      });
      if (!items.length) {
        toast.info("Nessun valore inserito");
        return;
      }
      await api.post(`${getApiPrefix()}/cdc-manodopera/batch`, { items });
      toast.success(`${items.length} righe salvate`);
      await fetchRecords();
    } catch (e) {
      handleApiError(e);
    } finally { setSaving(false); }
  };

  const totaleAnno = useMemo(() => {
    let tot = 0;
    Object.values(recordsByKey).forEach(r => {
      const v = parseFloat(String(r?.ore || 0).replace(",", "."));
      if (!isNaN(v)) tot += v;
    });
    return tot;
  }, [recordsByKey]);

  return (
    <div className="space-y-6" data-testid="manodopera-cdc-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Clock className="h-7 w-7" /> Manodopera per CDC
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Inserisci le ore mensili di manodopera per ogni Centro di Costo. Saranno usate dal Bilancio Competenza per calcolare il costo orario (€/h).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">{t('common.year')}</Label>
            <Select value={anno} onValueChange={setAnno}>
              <SelectTrigger className="w-32" data-testid="select-anno"><SelectValue /></SelectTrigger>
              <SelectContent>
                {annoOptions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleSaveAll} disabled={saving} data-testid="save-manodopera-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Salva tutto
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Ore per CDC e mese - {anno}</CardTitle>
            <span className="text-sm text-muted-foreground">Totale anno: <strong>{totaleAnno.toFixed(2)} h</strong></span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : centriCosto.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4">Nessun centro di costo configurato</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2 font-medium sticky left-0 bg-muted/50 min-w-[220px]">{t('costCenters.title')}</th>
                    {MESI.map(m => (
                      <th key={m.num} className="p-2 font-medium text-center min-w-[80px]">{m.label.substring(0,3)}</th>
                    ))}
                    <th className="p-2 font-medium text-right min-w-[90px]">{t('common.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {centriCosto.map(cc => {
                    const totaleCdc = MESI.reduce((sum, m) => {
                      const v = parseFloat(String(recordsByKey[`${cc.id}_${anno}-${m.num}`]?.ore || 0).replace(",", "."));
                      return sum + (isNaN(v) ? 0 : v);
                    }, 0);
                    return (
                      <tr key={cc.id} className="border-b hover:bg-muted/30">
                        <td className="p-2 sticky left-0 bg-background">
                          <div className="font-medium">{cc.codice}</div>
                          <div className="text-xs text-muted-foreground">{cc.nome}</div>
                        </td>
                        {MESI.map(m => {
                          const key = `${cc.id}_${anno}-${m.num}`;
                          const rec = recordsByKey[key];
                          return (
                            <td key={m.num} className="p-1">
                              <Input
                                type="number" min="0" step="0.5"
                                className="h-8 text-right text-sm"
                                value={rec?.ore ?? ""}
                                onChange={(e) => setOre(cc.id, `${anno}-${m.num}`, e.target.value)}
                                placeholder="0"
                                data-testid={`ore-${cc.codice}-${m.num}`}
                              />
                            </td>
                          );
                        })}
                        <td className="p-2 text-right font-semibold">{totaleCdc.toFixed(1)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 bg-muted/30 font-semibold">
                    <td className="p-2 sticky left-0 bg-muted/30">Totale mese</td>
                    {MESI.map(m => {
                      const tot = centriCosto.reduce((sum, cc) => {
                        const v = parseFloat(String(recordsByKey[`${cc.id}_${anno}-${m.num}`]?.ore || 0).replace(",", "."));
                        return sum + (isNaN(v) ? 0 : v);
                      }, 0);
                      return <td key={m.num} className="p-2 text-center">{tot.toFixed(0)}</td>;
                    })}
                    <td className="p-2 text-right">{totaleAnno.toFixed(1)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
